import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from '@tanstack/react-router';
import { ENTITY_URL_PARAMS, type GlobalPanelEntityType } from '@/store/panel-stack-store';

/**
 * Maps old URL paths to entity types for backward compatibility.
 *
 * Old pattern: /contacts?id=abc
 * New pattern: /contacts?panelStack=contact&contactId=abc
 */
const PATH_TO_ENTITY_TYPE: Record<string, GlobalPanelEntityType> = {
  '/jobs': 'job',
  '/companies': 'company',
  '/contacts': 'contact',
  '/sequences': 'sequence',
  '/tasks': 'task',
};

/**
 * Hook that detects old `?id=` URL pattern on entity list pages and redirects
 * to the new `?panelStack=type&typeId=id` format.
 *
 * This provides backward compatibility during migration and ensures
 * shared links using the old format continue to work.
 *
 * @example
 * // In GlobalDetailPanelProviderInner:
 * useLegacyPanelRedirect();
 */
export function useLegacyPanelRedirect(): void {
  const navigate = useNavigate();
  const location = useLocation();
  const pathname = location.pathname;
  const search = location.search as unknown as Record<string, unknown>;
  const hasRedirected = useRef(false);

  useEffect(() => {
    // Only run once per page load to avoid redirect loops
    if (hasRedirected.current) return;

    // Check if this path supports legacy redirect
    const entityType = PATH_TO_ENTITY_TYPE[pathname];
    if (!entityType) return;

    // Check for old ?id= pattern
    const legacyId = search.id as string | undefined;
    if (!legacyId) return;

    // Check if already using new pattern (has panelStack param)
    if (search.panelStack) return;

    // Mark as redirected to prevent loops
    hasRedirected.current = true;

    // Build new search object with panelStack format
    const newSearch = { ...search };

    // Remove old id param
    delete newSearch.id;

    // Add new panel params
    newSearch.panelStack = entityType;
    const entityIdParam = ENTITY_URL_PARAMS[entityType];
    newSearch[entityIdParam] = legacyId;

    // Redirect using replace to avoid duplicate history entries
    navigate({
      to: pathname,
      search: newSearch,
      replace: true,
    });
  }, [pathname, search, navigate]);
}
